// Everything the Library reads, through the hub's tables and functions as the member themselves
// (docs/data-model.md, sections 6 and 8). Row level security decides what comes back: a member gets
// their own rows, the products they own and every live free item not held back from them. Members
// never read hub.access: their access comes through hub.my_access(). A staff member at 2-step login
// reads more, so model.js narrows everything to the person's own access before drawing.
import { supabase, must } from '../lib/supabase.js'
import { loadBalances } from '../lib/billing.js'
import { now } from '../lib/clock.js'
import { loadMyLog } from '../products/accelerator/data.js'
import { peekLog } from '../products/accelerator/useMastermind.js'
import { LEVEL_FRESH_MS, LEVEL_WAIT_MS, levelOfLog, levelProducts } from './levels.js'

const q = (table, columns) => supabase.from(table).select(columns)

// How long the Library waits for Stripe's amounts for a secured place's balance before it shows without them
const BALANCES_WAIT_MS = 1500

// A promise's answer if it comes within ms, else null
function inTime(promise, ms) {
  let timer
  const late = new Promise(resolve => { timer = setTimeout(() => resolve(null), ms) })
  return Promise.race([promise, late]).finally(() => clearTimeout(timer))
}

export async function loadLibrary(personId) {
  const [
    access, products, cohorts, modules, sessions, recordings, content, progress,
    homework, ticks, steps, stepProgress, state, webinars, announcements, hides,
    notifications, placements, items, sections, spaces, forLife, elsewhere,
  ] = await Promise.all([
    supabase.rpc('my_access').then(must),
    // cover_path: a free product's own cover photo, shown on its Free resources card and Library tile
    // when 1 is set (3 Nov), signed by sign-file's covers branch
    q('catalogue', 'id, slug, path, name, tagline, type, access_kind, state, look, cover_path, moved_in, term_months, sort_order, live_sessions_audience, join_opens_minutes, settings').then(must),
    q('cohorts', 'id, product_id, name, kind, starts_on, ends_on, weeks_total').then(must),
    q('modules', 'id, product_id, key, number, label, title, sort_order, is_published').then(must),
    // the Zoom link is for Coming up's Join, which shows from join_opens_minutes before; row level
    // security returns only the sessions they can join (their own intake's, can_see_session)
    q('sessions', 'id, product_id, cohort_id, module_id, number, title, starts_at, duration_minutes, status, zoom_join_url').then(must),
    // recorded_on and thumbnail_path: a recording's own thumbnail on the search's recording hits, or its
    // date on the grey panel where it has none (decision 47, 12 Oct; migration 20261012200000)
    q('recordings', 'id, product_id, module_id, cohort_id, key, title, sort_order, is_published, published_at, recorded_on, thumbnail_path').then(must),
    q('recording_content', 'recording_id, duration_seconds, chapters').then(must),
    q('recording_progress', 'recording_id, position_seconds, furthest_seconds, watched_at, last_played_at').eq('person_id', personId).then(must),
    q('homework_items', 'id, product_id, module_id, removed_at').then(must),
    q('homework_ticks', 'item_id').eq('person_id', personId).then(must),
    q('steps', 'id, product_id, section, title, sort_order, is_published').then(must),
    q('step_progress', 'step_id, done_at').eq('person_id', personId).then(must),
    q('member_product_state', 'product_id, last_opened_at, finished_at').eq('person_id', personId).then(must),
    // thumbnail_path: Adam's own thumbnail for the card (Adam, 9 Oct), signed by sign-file only when 1 is set
    q('webinars', 'id, product_id, title, starts_at, duration_minutes, shown, replay_up, thumbnail_path').then(must),
    q('announcements', 'id, headline, body_line, button_label, button_path, audience, product_id, starts_at, comes_down_at, taken_down_at').then(must),
    q('announcement_hides', 'announcement_id').eq('person_id', personId).then(must),
    q('notifications', 'id, kind, product_id, body, path, created_at, read_at').eq('person_id', personId)
      .order('created_at', { ascending: false }).limit(20).then(must),
    q('resource_placements', 'id, item_id, product_id, section_id, module_id, title_override, release_kind, removed_at, sort_order').then(must),
    q('resource_items', 'id, kind, title').then(must),
    q('resource_sections', 'id, product_id, name').then(must),
    q('client_spaces', 'id, product_id, client_person_id, opened_at, ended_at').eq('client_person_id', personId).then(must),
    // the products whose access has ended that keep yours-for-life items (hub.my_for_life, Adam, 3 Oct)
    supabase.rpc('my_for_life').then(must),
    // the products still on their own sites that they have an account for there, for the portal cards
    // (hub.my_elsewhere, Adam, 10 Oct); a project without the function yet gives none, so the Library
    // never fails for want of it
    supabase.rpc('my_elsewhere').then(must).catch(() => []),
  ])

  // A secured place's pay link and due date (hub.my_secured_places) and its balance's amount (stripe-prices,
  // read from Stripe, never stored), for Next up's pay button and Account's Billing and My programmes (member
  // review, 11 Oct). Asked only of someone with a secured place; while it's on its way the rest is read. If
  // either can't be read, the Library still shows, with no amount, and Next up opens the product's page.
  // Stripe is waited for BALANCES_WAIT_MS at most, so no page waits on it: after that the Library shows
  // without the amount and balancesLate (not drawn) brings it when Stripe answers (app/member.jsx).
  const secured = access.some(a => a.status === 'secured')
  const placesRead = secured ? supabase.rpc('my_secured_places').then(must).catch(() => []) : Promise.resolve([])
  const balancesAsk = secured ? loadBalances(personId).catch(() => []) : null
  const balancesInTime = balancesAsk ? inTime(balancesAsk, BALANCES_WAIT_MS) : Promise.resolve([])

  // The product a replay or webinar sells: its name and look, which a non-owner can't read from the
  // catalogue (hub.my_replay_cta). Only for the webinars this person can see.
  const ctas = {}
  await Promise.all(webinars.map(async w => {
    const rows = await supabase.rpc('my_replay_cta', { p_webinar: w.id }).then(must)
    if (rows && rows[0]) ctas[w.id] = rows[0]
  }))

  // a mastermind's month of its term, as its home counts it (hub.my_mastermind, every mastermind open
  // to them), for the tile and Account; asked only of someone with 1 open (it answers nothing else)
  const masterminds = new Set(products.filter(p => p.type === 'mastermind').map(p => p.id))
  const mastermind = access.some(a => masterminds.has(a.product_id) && ['active', 'overdue'].includes(a.status))
    ? await supabase.rpc('my_mastermind', { p_product: null }).then(must).catch(() => [])
    : []

  const openSpaces = spaces.filter(s => !s.ended_at)
  const clientSessions = openSpaces.length
    ? await q('client_sessions', 'id, space_id, number, title, starts_at, duration_minutes, zoom_join_url').in('space_id', openSpaces.map(s => s.id)).then(must)
    : []
  const [places, balances] = await Promise.all([placesRead, balancesInTime])

  return {
    personId, access, products, cohorts, modules, sessions, recordings, content, progress,
    homework, ticks, steps, stepProgress, state, webinars, ctas, announcements, hides,
    notifications, placements, items, sections, spaces: openSpaces, clientSessions, forLife: forLife || [],
    mastermind: mastermind || [], elsewhere: elsewhere || [], places: places || [], balances: balances || [],
    // Stripe's amounts still on their way when the wait ran out, or null
    balancesLate: balances === null ? balancesAsk : null,
  }
}

// Where they stand on a mastermind's tile (levels.js; the Accelerator critique's member 17, 22 Oct): { [product id]:
// level key } for every product levelProducts names, leaving out any whose read failed, came after LEVEL_WAIT_MS or is
// Getting started. Called once the Library has shown, never awaited by it (app/member.jsx), and never throws. Cheap: the
// log the Accelerator's own pages last read (useMastermind.js peekLog), or the Library's own last read, is used again
// while it's under LEVEL_FRESH_MS old, so going between the Library and the home doesn't read it again; else their own
// log (hub.my_accountability_log), 1 read a product.
const levelReads = new Map()
// a read still on its way, so a second ask while it is (the Library's rows loaded again) doesn't read again
const levelAsks = new Map()
async function levelFor(personId, productId, wait) {
  const k = `${personId}:${productId}`
  const seen = peekLog(productId, personId)
  const newest = [seen && { key: levelOfLog(seen.log), at: seen.at }, levelReads.get(k)].filter(Boolean).sort((a, b) => b.at - a.at)[0]
  if (newest && Date.now() - newest.at < LEVEL_FRESH_MS) return newest.key
  // kept when it comes, even after the wait, for the next visit
  if (!levelAsks.has(k)) {
    levelAsks.set(k, loadMyLog(productId)
      .then(doc => { const key = levelOfLog(doc); levelReads.set(k, { key, at: Date.now() }); return key })
      .catch(() => null)
      .finally(() => levelAsks.delete(k)))
  }
  return inTime(levelAsks.get(k), wait)
}
export async function loadLevels(d, { wait = LEVEL_WAIT_MS } = {}) {
  try {
    const ids = levelProducts(d, now())
    if (!ids.length || !d.personId) return {}
    const keys = await Promise.all(ids.map(id => levelFor(d.personId, id, wait).catch(() => null)))
    return Object.fromEntries(ids.map((id, i) => [id, keys[i]]).filter(([, key]) => key))
  } catch {
    return {}
  }
}

export async function hideAnnouncement(personId, announcementId) {
  must(await supabase.from('announcement_hides').insert({ person_id: personId, announcement_id: announcementId }))
}

// Opening the bell marks what's in it as read, so the dot goes
export async function markNotificationsRead(personId) {
  must(await supabase.from('notifications').update({ read_at: new Date().toISOString() })
    .eq('person_id', personId).is('read_at', null))
}
